import { Search, Sun, Moon } from 'lucide-react';

interface ProposalTopbarProps {
  brandName: string;
  projectName: string;
  topNavLinks?: { id: string; label: string }[];
  currentPage: string;
  theme: 'dark' | 'light';
  onNavigate: (pageId: string) => void;
  onToggleTheme: () => void;
  onOpenSearch: () => void;
  onOpenModal: () => void;
}

export function ProposalTopbar({
  brandName,
  projectName,
  topNavLinks,
  currentPage,
  theme,
  onNavigate,
  onToggleTheme,
  onOpenSearch,
  onOpenModal,
}: ProposalTopbarProps) {
  return (
    <header className="fixed top-0 left-0 right-0 h-[56px] bg-proposal-bg border-b border-proposal-border flex items-center px-5 gap-4 z-[100] backdrop-blur-sm">
      {/* Logo */}
      <a className="flex items-center gap-2.5 no-underline shrink-0 cursor-pointer" onClick={() => onNavigate('home')}>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect width="32" height="32" rx="6" fill="var(--p-accent)" />
          <text x="16" y="22" textAnchor="middle" fontSize="16" fontWeight="700" fill="var(--p-accent-text)">C</text>
        </svg>
        <span className="text-sm font-semibold text-proposal-fg1 whitespace-nowrap">{brandName}</span>
      </a>

      {/* Divider */}
      <div className="w-px h-5 bg-proposal-border-strong shrink-0" />

      {/* Project name */}
      <span className="text-[13px] font-semibold text-proposal-fg2 whitespace-nowrap shrink-0">{projectName}</span>

      {/* Search */}
      <div className="flex-1 max-w-[280px]">
        <button
          onClick={onOpenSearch}
          className="w-full flex items-center gap-2 bg-proposal-bg3 border border-proposal-border rounded-p-md px-3 py-1.5 text-proposal-fg3 text-[13px] cursor-pointer transition-colors hover:border-proposal-border-strong font-proposal-ui"
        >
          <Search size={14} />
          Buscar...
          <kbd className="ml-auto font-proposal-mono text-[11px] bg-proposal-bg4 border border-proposal-border-strong rounded px-1.5 py-px text-proposal-fg3">⌘K</kbd>
        </button>
      </div>

      {/* Nav links */}
      {topNavLinks && topNavLinks.length > 0 && (
        <nav className="flex items-center gap-1 ml-auto">
          {topNavLinks.map((link) => (
            <a
              key={link.id}
              onClick={() => onNavigate(link.id)}
              className={`text-[13px] font-medium px-2.5 py-1 rounded-p-sm cursor-pointer transition-colors ${
                currentPage === link.id ? 'text-proposal-accent' : 'text-proposal-fg2 hover:text-proposal-fg1 hover:bg-proposal-bg3'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>
      )}

      {/* Theme toggle */}
      <button
        onClick={onToggleTheme}
        className="w-8 h-8 flex items-center justify-center rounded-p-md bg-transparent border border-proposal-border text-proposal-fg2 cursor-pointer transition-colors hover:bg-proposal-bg3 shrink-0"
      >
        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
      </button>

      {/* Accept button */}
      <button
        onClick={onOpenModal}
        className="bg-proposal-accent text-proposal-accent-text border-none rounded-p-md px-3.5 py-1.5 text-[13px] font-semibold cursor-pointer whitespace-nowrap transition-opacity hover:opacity-85 shrink-0 font-proposal-ui"
      >
        Aceptar propuesta
      </button>
    </header>
  );
}
